import {
  clampDateTo,
  datePl,
  type FleetReportDto,
  OBLIGATION_RULES,
  OBLIGATION_SOURCES,
  type ObligationDto,
  type ObligationRule,
  type ObligationSource,
  RECURRENCE_BASIS,
  type RecurrenceBasis,
  REPORT_OPEN,
  todayInWarsaw,
  type VehicleDto,
  type VisitDto,
} from '@bqcrm/shared';
import { Alert, Button, Checkbox, Group, Menu, Modal, NumberInput, Select, SimpleGrid, Stack, Switch, Table, Text, Textarea, TextInput } from '@mantine/core';
import { IconCalendarPlus, IconDots, IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api } from '../../../api';
import { ask } from '../../../confirm';
import { ObligationStateBadge, ObligationStatusBadge, VisitStatusBadge } from '../../../components/Badges';
import { Money } from '../../../components/Money';
import { QueryState } from '../../../components/QueryState';
import { EmptyState, SectionCard } from '../../../components/ui';
import { dayRange } from '../../../format';
import { notifyError, notifyOk } from '../../../notify';
import { kmPl, useActivityTypes, useFleetInvalidate } from '../common';

const num = (v: string | number) => (typeof v === 'number' ? v : null);

function ObligationModal({ v, obligation, onClose }: { v: VehicleDto; obligation?: ObligationDto; onClose: () => void }) {
  const types = useActivityTypes();
  const invalidate = useFleetInvalidate();
  const [typeId, setTypeId] = useState<string | null>(obligation?.typeId ?? null);
  const [rule, setRule] = useState<ObligationRule>(obligation?.rule ?? 'data');
  const [dueDate, setDueDate] = useState(obligation?.dueDate ?? '');
  const [dueKm, setDueKm] = useState<number | null>(obligation?.dueKm ?? null);
  const [everyMonths, setEveryMonths] = useState<number | null>(obligation?.everyMonths ?? null);
  const [everyKm, setEveryKm] = useState<number | null>(obligation?.everyKm ?? null);
  const [basis, setBasis] = useState<RecurrenceBasis>(obligation?.basis ?? 'wykonanie');
  const [source, setSource] = useState<ObligationSource>(obligation?.source ?? 'reczna');
  const [note, setNote] = useState(obligation?.note ?? '');
  const pickType = (id: string | null) => {
    setTypeId(id);
    const t = types.data?.find((x) => x.id === id);
    if (t && !obligation) setRule(t.rule);
  };
  const hint = types.data?.find((x) => x.id === typeId);
  const save = useMutation({
    mutationFn: () => {
      const body = { rule, dueDate: rule === 'przebieg' ? null : dueDate || null, dueKm: rule === 'data' ? null : dueKm, everyMonths, everyKm, basis, source, note };
      return obligation ? api(`/fleet/obligations/${obligation.id}`, { method: 'PUT', body }) : api(`/fleet/vehicles/${v.id}/obligations`, { method: 'POST', body: { ...body, typeId } });
    },
    onSuccess: () => {
      notifyOk(obligation ? 'Zapisano czynność' : 'Dodano czynność');
      invalidate();
      onClose();
    },
    onError: notifyError,
  });
  const valid = (!!typeId || !!obligation) && ((rule === 'data' && !!dueDate) || (rule === 'przebieg' && dueKm !== null) || (rule === 'wczesniej' && (!!dueDate || dueKm !== null)));
  return (
    <Modal opened onClose={onClose} title={obligation ? `Edycja czynności: ${obligation.typeName}` : 'Nowa czynność'} size="lg">
      <Stack gap="sm">
        {!obligation && <Select label="Rodzaj czynności" required searchable data={(types.data ?? []).filter((t) => t.active).map((t) => ({ value: t.id, label: t.name }))} value={typeId} onChange={pickType} />}
        <Select label="Sposób wyznaczania" data={Object.entries(OBLIGATION_RULES).map(([value, label]) => ({ value, label }))} value={rule} onChange={(x) => x && setRule(x as ObligationRule)} allowDeselect={false} />
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          {rule !== 'przebieg' && <TextInput label="Termin" type="date" required={rule === 'data'} value={dueDate} onChange={(e) => setDueDate(e.currentTarget.value)} />}
          {rule !== 'data' && <NumberInput label="Limit przebiegu" required={rule === 'przebieg'} suffix=" km" min={0} thousandSeparator=" " allowDecimal={false} value={dueKm ?? ''} onChange={(x) => setDueKm(num(x))} />}
        </SimpleGrid>
        <Text size="sm" fw={600} mt={4}>
          Cykl (opcjonalnie)
        </Text>
        <Text size="xs" c="dimmed" mt={-8}>
          Po wykonaniu powstanie następna czynność według tego cyklu. Bez cyklu — czynność jednorazowa.
          {hint && (hint.everyMonths || hint.everyKm) ? ` Podpowiedź z ustawień: ${[hint.everyMonths ? `co ${hint.everyMonths} mies.` : null, hint.everyKm ? `co ${kmPl(hint.everyKm)}` : null].filter(Boolean).join(' / ')}.` : ''}
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
          <NumberInput label="Co ile miesięcy" min={1} max={240} allowDecimal={false} value={everyMonths ?? ''} onChange={(x) => setEveryMonths(num(x))} />
          <NumberInput label="Co ile km" min={100} thousandSeparator=" " allowDecimal={false} value={everyKm ?? ''} onChange={(x) => setEveryKm(num(x))} />
          <Select label="Liczone" data={Object.entries(RECURRENCE_BASIS).map(([value, label]) => ({ value, label }))} value={basis} onChange={(x) => x && setBasis(x as RecurrenceBasis)} allowDeselect={false} disabled={!everyMonths && !everyKm} />
        </SimpleGrid>
        <Select label="Źródło terminu" data={Object.entries(OBLIGATION_SOURCES).map(([value, label]) => ({ value, label }))} value={source} onChange={(x) => x && setSource(x as ObligationSource)} allowDeselect={false} w={{ base: '100%', sm: 260 }} />
        <Textarea label="Uwagi" autosize minRows={2} value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>
            {obligation ? 'Zapisz' : 'Dodaj czynność'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function RescheduleModal({ o, onClose }: { o: ObligationDto; onClose: () => void }) {
  const invalidate = useFleetInvalidate();
  const [dueDate, setDueDate] = useState(o.dueDate ?? '');
  const [dueKm, setDueKm] = useState<number | null>(o.dueKm);
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () => api(`/fleet/obligations/${o.id}/reschedule`, { method: 'POST', body: { dueDate: o.rule === 'przebieg' ? null : dueDate || null, dueKm: o.rule === 'data' ? null : dueKm, reason } }),
    onSuccess: () => {
      notifyOk('Przesunięto termin');
      invalidate();
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title={`Przesunięcie terminu: ${o.typeName}`}>
      <Stack gap="sm">
        <Alert color="blue" variant="light" py="xs">
          Przesunięcie zmienia tylko termin (zostaje w historii) — <b>nie oznacza wykonania</b>. Gdy czynność została zrobiona, użyj „Oznacz jako wykonaną”.
        </Alert>
        {o.rule !== 'przebieg' && <TextInput label="Nowy termin" type="date" value={dueDate} onChange={(e) => setDueDate(e.currentTarget.value)} />}
        {o.rule !== 'data' && <NumberInput label="Nowy limit przebiegu" suffix=" km" min={0} thousandSeparator=" " allowDecimal={false} value={dueKm ?? ''} onChange={(x) => setDueKm(num(x))} />}
        <Textarea label="Powód (opcjonalnie)" autosize minRows={2} value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button loading={save.isPending} disabled={!dueDate && dueKm === null} onClick={() => save.mutate()}>
            Przesuń termin
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function CompleteModal({ o, onClose }: { o: ObligationDto; onClose: () => void }) {
  const invalidate = useFleetInvalidate();
  const [doneDate, setDoneDate] = useState(todayInWarsaw());
  const [doneKm, setDoneKm] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const cyclic = !!o.everyMonths || !!o.everyKm;
  const [scheduleNext, setScheduleNext] = useState(true);
  const save = useMutation({
    mutationFn: () => api(`/fleet/obligations/${o.id}/complete`, { method: 'POST', body: { doneDate, doneKm, comment, scheduleNext: cyclic && scheduleNext } }),
    onSuccess: () => {
      notifyOk('Oznaczono jako wykonaną');
      invalidate();
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title={`Wykonanie: ${o.typeName}`}>
      <Stack gap="sm">
        <TextInput label="Data wykonania" type="date" required max={todayInWarsaw()} value={doneDate} onChange={(e) => setDoneDate(e.currentTarget.value)} />
        <NumberInput label="Przebieg przy wykonaniu" description="Zapisze się też jako odczyt przebiegu" suffix=" km" min={0} thousandSeparator=" " allowDecimal={false} value={doneKm ?? ''} onChange={(x) => setDoneKm(num(x))} />
        <Textarea label="Komentarz" autosize minRows={2} value={comment} onChange={(e) => setComment(e.currentTarget.value)} />
        {cyclic && (
          <Switch
            checked={scheduleNext}
            onChange={(e) => setScheduleNext(e.currentTarget.checked)}
            label="Zaplanuj następną według cyklu"
            description={`${[o.everyMonths ? `co ${o.everyMonths} mies.` : null, o.everyKm ? `co ${kmPl(o.everyKm)}` : null].filter(Boolean).join(' / ')}, ${RECURRENCE_BASIS[o.basis]}`}
          />
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button color="green" disabled={!doneDate} loading={save.isPending} onClick={() => save.mutate()}>
            Oznacz jako wykonaną
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function CancelModal({ o, onClose }: { o: ObligationDto; onClose: () => void }) {
  const invalidate = useFleetInvalidate();
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () => api(`/fleet/obligations/${o.id}/cancel`, { method: 'POST', body: { reason: reason.trim() } }),
    onSuccess: () => {
      notifyOk('Anulowano czynność');
      invalidate();
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title={`Anulowanie czynności: ${o.typeName}`}>
      <Stack gap="sm">
        <Text size="sm">Anulowana czynność nie tworzy następnej i znika z przypomnień.</Text>
        <Textarea label="Powód" required autosize minRows={2} value={reason} onChange={(e) => setReason(e.currentTarget.value)} data-autofocus />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Wróć
          </Button>
          <Button color="red" disabled={reason.trim().length < 3} loading={save.isPending} onClick={() => save.mutate()}>
            Anuluj czynność
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function VisitModal({ v, obligations, reports, preselect, onClose }: { v: VehicleDto; obligations: ObligationDto[]; reports: FleetReportDto[]; preselect?: string[]; onClose: () => void }) {
  const invalidate = useFleetInvalidate();
  const [dateFrom, setDateFrom] = useState(todayInWarsaw());
  const [dateTo, setDateTo] = useState(todayInWarsaw());
  const [workshop, setWorkshop] = useState('');
  const [note, setNote] = useState('');
  const [obligationIds, setObligationIds] = useState<string[]>(preselect ?? []);
  const [reportIds, setReportIds] = useState<string[]>([]);
  const [unavailable, setUnavailable] = useState(true);
  const save = useMutation({
    mutationFn: () => api(`/fleet/vehicles/${v.id}/visits`, { method: 'POST', body: { dateFrom, dateTo, workshop, note, obligationIds, reportIds, unavailable } }),
    onSuccess: () => {
      notifyOk('Umówiono wizytę');
      invalidate();
      onClose();
    },
    onError: notifyError,
  });
  const toggle = (list: string[], set: (l: string[]) => void, id: string) => set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  return (
    <Modal opened onClose={onClose} title="Nowa wizyta w serwisie" size="lg">
      <Stack gap="sm">
        <Group grow>
          <TextInput label="Od" type="date" required value={dateFrom} onChange={(e) => {
            const f = e.currentTarget.value;
            setDateFrom(f);
            setDateTo((t) => clampDateTo(f, t));
          }} />
          <TextInput label="Do" type="date" required min={dateFrom || undefined} value={dateTo} onChange={(e) => setDateTo(e.currentTarget.value)} />
        </Group>
        <TextInput label="Warsztat" value={workshop} onChange={(e) => setWorkshop(e.currentTarget.value)} />
        {obligations.length > 0 && (
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Czynności
            </Text>
            {obligations.map((o) => (
              <Checkbox key={o.id} label={`${o.typeName}${o.dueDate ? ` · ${datePl(o.dueDate)}` : ''}${o.dueKm !== null ? ` · ${kmPl(o.dueKm)}` : ''}`} checked={obligationIds.includes(o.id)} onChange={() => toggle(obligationIds, setObligationIds, o.id)} />
            ))}
          </Stack>
        )}
        {reports.length > 0 && (
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Zgłoszenia
            </Text>
            {reports.map((r) => (
              <Checkbox key={r.id} label={`${r.number} — ${r.description.slice(0, 80)}`} checked={reportIds.includes(r.id)} onChange={() => toggle(reportIds, setReportIds, r.id)} />
            ))}
          </Stack>
        )}
        <Switch label="Pojazd niedostępny w czasie wizyty" description="Utworzy wpis niedostępności (widoczny w Kalendarzu i przy planowaniu)" checked={unavailable} onChange={(e) => setUnavailable(e.currentTarget.checked)} />
        <Textarea label="Uwagi" autosize minRows={2} value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button disabled={!dateFrom || !dateTo} loading={save.isPending} onClick={() => save.mutate()}>
            Umów wizytę
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function VisitCompleteModal({ visit, onClose }: { visit: VisitDto; onClose: () => void }) {
  const invalidate = useFleetInvalidate();
  const [doneDate, setDoneDate] = useState(visit.dateTo <= todayInWarsaw() ? visit.dateTo : todayInWarsaw());
  const [km, setKm] = useState<number | null>(null);
  const openOb = visit.obligations.filter((o) => o.status === 'otwarta' || o.status === 'umowiona');
  const openRep = visit.reports.filter((r) => REPORT_OPEN.includes(r.status));
  const [obIds, setObIds] = useState<string[]>(openOb.map((o) => o.id));
  const [repIds, setRepIds] = useState<string[]>(openRep.map((r) => r.id));
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () => api(`/fleet/visits/${visit.id}/complete`, { method: 'POST', body: { doneDate, km, doneObligationIds: obIds, doneReportIds: repIds, note } }),
    onSuccess: () => {
      notifyOk('Zakończono wizytę');
      invalidate();
      onClose();
    },
    onError: notifyError,
  });
  const toggle = (list: string[], set: (l: string[]) => void, id: string) => set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  return (
    <Modal opened onClose={onClose} title="Zakończenie wizyty w serwisie" size="lg">
      <Stack gap="sm">
        <Group grow>
          <TextInput label="Data zakończenia" type="date" required value={doneDate} onChange={(e) => setDoneDate(e.currentTarget.value)} />
          <NumberInput label="Przebieg na wizycie" suffix=" km" min={0} thousandSeparator=" " allowDecimal={false} value={km ?? ''} onChange={(x) => setKm(num(x))} />
        </Group>
        {openOb.length > 0 && (
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Wykonane czynności
            </Text>
            <Text size="xs" c="dimmed">
              Niezaznaczone wracają do „do wykonania”.
            </Text>
            {openOb.map((o) => (
              <Checkbox key={o.id} label={o.typeName} checked={obIds.includes(o.id)} onChange={() => toggle(obIds, setObIds, o.id)} />
            ))}
          </Stack>
        )}
        {openRep.length > 0 && (
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Zakończone zgłoszenia
            </Text>
            {openRep.map((r) => (
              <Checkbox key={r.id} label={r.number} checked={repIds.includes(r.id)} onChange={() => toggle(repIds, setRepIds, r.id)} />
            ))}
          </Stack>
        )}
        <Textarea label="Uwagi" autosize minRows={2} value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button color="green" disabled={!doneDate} loading={save.isPending} onClick={() => save.mutate()}>
            Zakończ wizytę
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

type Action = { kind: 'edit' | 'reschedule' | 'complete' | 'cancel'; o: ObligationDto };

/** Terminy i serwisy: czynności (termin / przebieg / co wcześniej) i wizyty w serwisie. */
export function MaintenanceTab({ v, highlight }: { v: VehicleDto; highlight: string | null }) {
  const obl = useQuery({ queryKey: ['fleet', 'vehicle', v.id, 'obligations'], queryFn: () => api<ObligationDto[]>(`/fleet/vehicles/${v.id}/obligations`) });
  const visits = useQuery({ queryKey: ['fleet', 'vehicle', v.id, 'visits'], queryFn: () => api<VisitDto[]>(`/fleet/vehicles/${v.id}/visits`) });
  const reports = useQuery({ queryKey: ['fleet', 'reports', v.id], queryFn: () => api<FleetReportDto[]>(`/fleet/reports?vehicleId=${v.id}`) });
  const invalidate = useFleetInvalidate();
  const [adding, setAdding] = useState(false);
  const [action, setAction] = useState<Action | null>(null);
  const [visitFor, setVisitFor] = useState<string[] | null>(null);
  const [completing, setCompleting] = useState<VisitDto | null>(null);
  const [showDone, setShowDone] = useState(false);
  const rowRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    if (highlight && obl.data) rowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [highlight, obl.data]);
  const visitStatus = useMutation({
    mutationFn: (b: { id: string; status: 'planowana' | 'w_trakcie' | 'odwolana' }) => api(`/fleet/visits/${b.id}/status`, { method: 'POST', body: { status: b.status, note: '' } }),
    onSuccess: () => {
      notifyOk('Zmieniono status wizyty');
      invalidate();
    },
    onError: notifyError,
  });
  const manage = v.can.manage;
  const open = (obl.data ?? []).filter((o) => o.status === 'otwarta' || o.status === 'umowiona');
  const closed = (obl.data ?? []).filter((o) => o.status === 'wykonana' || o.status === 'anulowana');
  const rows = showDone ? [...open, ...closed] : open;
  const openReports = (reports.data ?? []).filter((r) => REPORT_OPEN.includes(r.status));
  const activeVisits = (visits.data ?? []).filter((x) => x.status === 'planowana' || x.status === 'w_trakcie');
  const pastVisits = (visits.data ?? []).filter((x) => x.status === 'zakonczona' || x.status === 'odwolana');

  return (
    <Stack gap="md">
      <SectionCard
        title="Czynności i terminy"
        padding={0}
        actions={
          <Group gap="xs">
            {closed.length > 0 && <Switch size="xs" label="Pokaż wykonane i anulowane" checked={showDone} onChange={(e) => setShowDone(e.currentTarget.checked)} />}
            {manage && (
              <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setAdding(true)}>
                Dodaj czynność
              </Button>
            )}
          </Group>
        }
      >
        <QueryState isLoading={obl.isLoading} error={obl.error}>
          {rows.length ? (
            <Table.ScrollContainer minWidth={860}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Czynność</Table.Th>
                    <Table.Th>Termin / limit</Table.Th>
                    <Table.Th>Stan</Table.Th>
                    <Table.Th>Cykl</Table.Th>
                    <Table.Th>Wizyta / wykonanie</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((o) => {
                    const isOpen = o.status === 'otwarta' || o.status === 'umowiona';
                    return (
                      <Table.Tr key={o.id} ref={o.id === highlight ? rowRef : undefined} style={o.id === highlight ? { backgroundColor: 'var(--mantine-color-yellow-light)' } : !isOpen ? { opacity: 0.6 } : undefined}>
                        <Table.Td>
                          <Text size="sm" fw={600}>
                            {o.typeName}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {OBLIGATION_RULES[o.rule]} · {OBLIGATION_SOURCES[o.source].toLowerCase()}
                            {o.note ? ` · ${o.note}` : ''}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{[o.dueDate ? datePl(o.dueDate) : null, o.dueKm !== null ? kmPl(o.dueKm) : null].filter(Boolean).join(' / ') || '—'}</Text>
                        </Table.Td>
                        <Table.Td>
                          {isOpen ? (
                            <>
                              <ObligationStateBadge state={o.eval.state} />
                              <Text size="xs" c="dimmed">
                                {o.eval.reason}
                              </Text>
                            </>
                          ) : (
                            <ObligationStatusBadge status={o.status} />
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{o.everyMonths || o.everyKm ? [o.everyMonths ? `co ${o.everyMonths} mies.` : null, o.everyKm ? `co ${kmPl(o.everyKm)}` : null].filter(Boolean).join(' / ') : 'jednorazowa'}</Text>
                          {(o.everyMonths || o.everyKm) && (
                            <Text size="xs" c="dimmed">
                              {RECURRENCE_BASIS[o.basis]}
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          {o.status === 'wykonana' ? (
                            <Text size="sm">
                              {datePl(o.doneDate)}
                              {o.doneKm !== null ? ` · ${kmPl(o.doneKm)}` : ''}
                              {o.doneByName ? ` · ${o.doneByName}` : ''}
                              {o.costGr ? (
                                <>
                                  {' · '}
                                  <Money gr={o.costGr} />
                                </>
                              ) : null}
                            </Text>
                          ) : o.visit ? (
                            <Group gap={4} wrap="nowrap">
                              <Text size="sm">
                                {dayRange(o.visit.dateFrom, o.visit.dateTo)}
                                {o.visit.workshop ? ` · ${o.visit.workshop}` : ''}
                              </Text>
                              <VisitStatusBadge status={o.visit.status} size="xs" />
                            </Group>
                          ) : (
                            <Text size="sm" c="dimmed">
                              —
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td ta="right">
                          {manage && isOpen && (
                            <Group gap={4} justify="flex-end" wrap="nowrap">
                              <Button size="compact-sm" variant="light" color="green" onClick={() => setAction({ kind: 'complete', o })}>
                                Wykonane
                              </Button>
                              <Menu position="bottom-end" withinPortal>
                                <Menu.Target>
                                  <Button size="compact-sm" variant="subtle" color="gray" aria-label="Więcej działań">
                                    <IconDots size={16} />
                                  </Button>
                                </Menu.Target>
                                <Menu.Dropdown>
                                  {!o.visit && (
                                    <Menu.Item leftSection={<IconCalendarPlus size={14} />} onClick={() => setVisitFor([o.id])}>
                                      Umów wizytę
                                    </Menu.Item>
                                  )}
                                  <Menu.Item onClick={() => setAction({ kind: 'reschedule', o })}>Przesuń termin</Menu.Item>
                                  <Menu.Item onClick={() => setAction({ kind: 'edit', o })}>Edytuj</Menu.Item>
                                  <Menu.Item color="red" onClick={() => setAction({ kind: 'cancel', o })}>
                                    Anuluj czynność
                                  </Menu.Item>
                                </Menu.Dropdown>
                              </Menu>
                            </Group>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          ) : (
            <EmptyState compact title="Brak czynności do wykonania" hint="Dodaj terminy: badanie techniczne, ubezpieczenie, serwis olejowy, wymianę opon — z datą, limitem przebiegu albo oboma." />
          )}
        </QueryState>
      </SectionCard>

      <SectionCard
        title="Wizyty w serwisie"
        padding={0}
        actions={
          manage && (
            <Button size="xs" variant="light" leftSection={<IconCalendarPlus size={14} />} onClick={() => setVisitFor([])}>
              Umów wizytę
            </Button>
          )
        }
      >
        <QueryState isLoading={visits.isLoading} error={visits.error}>
          {visits.data?.length ? (
            <Table.ScrollContainer minWidth={760}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Termin</Table.Th>
                    <Table.Th>Warsztat</Table.Th>
                    <Table.Th>Zakres</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {[...activeVisits, ...pastVisits].map((x) => (
                    <Table.Tr key={x.id} style={x.status === 'odwolana' ? { opacity: 0.6 } : undefined}>
                      <Table.Td>
                        <Text size="sm">{dayRange(x.dateFrom, x.dateTo)}</Text>
                        {x.km !== null && (
                          <Text size="xs" c="dimmed">
                            {kmPl(x.km)}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{x.workshop || '—'}</Text>
                        {x.note && (
                          <Text size="xs" c="dimmed">
                            {x.note}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{[...x.obligations.map((o) => o.typeName), ...x.reports.map((r) => r.number)].join(', ') || '—'}</Text>
                        {x.unavailabilityId && (
                          <Text size="xs" c="dimmed">
                            pojazd niedostępny w czasie wizyty
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <VisitStatusBadge status={x.status} />
                      </Table.Td>
                      <Table.Td ta="right">
                        {manage && (x.status === 'planowana' || x.status === 'w_trakcie') && (
                          <Group gap={4} justify="flex-end" wrap="nowrap">
                            {x.status === 'planowana' && (
                              <Button size="compact-sm" variant="subtle" onClick={() => visitStatus.mutate({ id: x.id, status: 'w_trakcie' })}>
                                W trakcie
                              </Button>
                            )}
                            <Button size="compact-sm" variant="light" color="green" onClick={() => setCompleting(x)}>
                              Zakończ
                            </Button>
                            <Button size="compact-sm" variant="subtle" color="red" onClick={async () => (await ask('Przypięte czynności wrócą do „do wykonania”.', { title: 'Odwołać wizytę?', confirm: 'Odwołaj', danger: true })) && visitStatus.mutate({ id: x.id, status: 'odwolana' })}>
                              Odwołaj
                            </Button>
                          </Group>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          ) : (
            <EmptyState compact title="Brak wizyt w serwisie" hint="Jedna wizyta może objąć kilka czynności i zgłoszeń." />
          )}
        </QueryState>
      </SectionCard>
      {!manage && (
        <Text size="xs" c="dimmed">
          Podgląd — zmiany wprowadza zarządzający flotą.
        </Text>
      )}
      {adding && <ObligationModal v={v} onClose={() => setAdding(false)} />}
      {action?.kind === 'edit' && <ObligationModal v={v} obligation={action.o} onClose={() => setAction(null)} />}
      {action?.kind === 'reschedule' && <RescheduleModal o={action.o} onClose={() => setAction(null)} />}
      {action?.kind === 'complete' && <CompleteModal o={action.o} onClose={() => setAction(null)} />}
      {action?.kind === 'cancel' && <CancelModal o={action.o} onClose={() => setAction(null)} />}
      {visitFor && <VisitModal v={v} obligations={open.filter((o) => !o.visit)} reports={openReports.filter((r) => !r.visitId)} preselect={visitFor} onClose={() => setVisitFor(null)} />}
      {completing && <VisitCompleteModal visit={completing} onClose={() => setCompleting(null)} />}
    </Stack>
  );
}
